import { Button, Form, Input, Modal, Select } from "antd";
import { useState } from "react";

/** A document the work order carries, as offered in the picker. */
export interface CommentableDocument {
  /** Numeric id the comments endpoint expects; absent for links. */
  id?: number;
  name: string;
}

interface Props {
  open: boolean;
  onCancel: () => void;
  /** `document` is undefined when none was chosen — picking one is optional. */
  onComment: (document: CommentableDocument | undefined, comment: string) => void;
  documents: CommentableDocument[];
  loading?: boolean;
}

const COMMENT_LIMIT = 5000;

/**
 * "Comments" — leaves a comment against one of the work order's documents via
 * `POST /workflow/comments`, which keys the comment by `documentId`. The
 * picker therefore carries the id, not just the display name; the caller sends
 * the whole document back so it can fill both fields of the payload.
 *
 * Reached from "Return with Comment" on the approval detail screen; the
 * resulting comment shows up on the Comments tab.
 */
export const CommentsModal = ({ open, onCancel, onComment, documents, loading }: Props) => {
  const [documentName, setDocumentName] = useState<string>("");
  const [comment, setComment] = useState("");

  const close = () => {
    setComment("");
    setDocumentName("");
    onCancel();
  };

  const selected = documents.find((doc) => doc.name === documentName);

  return (
    <Modal
      open={open}
      title="Comments"
      centered
      maskClosable={false}
      width={680}
      onCancel={close}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={close}>Cancel</Button>
          <Button
            type="primary"
            loading={loading}
            disabled={!comment.trim()}
            onClick={() => {
              onComment(documentName ? selected ?? { name: documentName } : undefined, comment.trim());
              setComment("");
              setDocumentName("");
            }}
          >
            Comment
          </Button>
        </div>
      }
    >
      <Form layout="vertical">
        <Form.Item label="Please choose the document you would like to comment on">
          <Select
            placeholder="Select a document (optional)"
            allowClear
            value={documentName || undefined}
            getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
            onChange={(value?: string) => setDocumentName(value ?? "")}
          >
            {documents.map((doc, index) => (
              <Select.Option
                value={doc.name}
                key={`${index}`}
              >
                {doc.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item required label="Please comment regarding the document">
          <Input.TextArea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            showCount
            maxLength={COMMENT_LIMIT}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
